import { combineReducers } from 'redux';
import { ActionType } from 'typesafe-actions';
import * as models from '../../models';
import * as actions from './actions';
import * as constants from './constants';

export interface ContactsStateShape {
    contact: models.Contact; 
    contactsResponse: models.ContactSimpleResponse;
    contactAuditLogs: models.ContactAuditLogResponse; 
    contactDupes: models.ConctactDupeResponse;
    isContactLoading: boolean; 
    isContactAuditsLoading: boolean; 
    isContactDupesLoading: boolean;
    isContactListLoading: boolean;     
    isContactMerging: boolean;
    isContactSyncing: boolean; 
}

export const contactsDefaultState: ContactsStateShape = {    
    contact: models.defaultContact,     
    contactsResponse: models.defaultResponse,
    contactAuditLogs: {
        ...models.defaultResponse,
        distinctUsers: []
    }, 
    contactDupes: models.defaultResponse, 
    isContactLoading: false, 
    isContactAuditsLoading: false, 
    isContactDupesLoading: false,
    isContactListLoading: false,     
    isContactMerging: false,
    isContactSyncing: false, 
}

export interface ContactListsShape {
    companyExaminersList: models.ContactSimpleResponse; 
    examinersList: models.ContactSimpleResponse;
    isCompanyExaminersListLoading: boolean; 
    isExaminersListLoading: boolean; 
    fieldAdjustersList: models.ContactSimpleResponse;
    isFieldAdjuestersListLoading: boolean; 
    fieldDirectorsList: models.ContactSimpleResponse;
    isFieldDirectorsListLoading: boolean;     
    reviewersList: models.ContactSimpleResponse;
    isReviewersListLoading: boolean; 
}

export const contactListsDefaultState: ContactListsShape = {
    companyExaminersList: models.defaultResponse, 
    examinersList: models.defaultResponse,
    isCompanyExaminersListLoading: false, 
    isExaminersListLoading: false, 
    fieldAdjustersList: models.defaultResponse, 
    isFieldAdjuestersListLoading: false, 
    fieldDirectorsList: models.defaultResponse,
    isFieldDirectorsListLoading: false,     
    reviewersList: models.defaultResponse,
    isReviewersListLoading: false,  
};  

export interface DropdownsShape {
    adjustersDD: models.DropdownListResponse; 
    companyExaminerDD: models.DropdownListResponse; 
    examinersDD: models.DropdownListResponse; 
    fieldDirectorsDD: models.DropdownListResponse; 
    isAdjustersDDLoading: boolean; 
    isCompanyExaminerDDLoading: boolean; 
    isExaminersDDLoading: boolean; 
    isFieldDirectorsDDLoading: boolean; 
    isReviewersDDLoading: boolean; 
    reviewersDD: models.DropdownListResponse; 
}

export const DropDownDefaultState: DropdownsShape = {
    adjustersDD: models.defaultResponse, 
    companyExaminerDD: models.defaultResponse, 
    examinersDD: models.defaultResponse,
    fieldDirectorsDD: models.defaultResponse,  
    isAdjustersDDLoading: false, 
    isCompanyExaminerDDLoading: false, 
    isExaminersDDLoading: false, 
    isFieldDirectorsDDLoading: false, 
    isReviewersDDLoading: false, 
    reviewersDD: models.defaultResponse, 
}

export interface ContactsSearchShape {
    contactListRequest: models.ContactsRequest; 
    contactListResponse: models.ContactSimpleResponse;
    isContactListLoading: boolean;
    isContactsDownloading: boolean;

}
export const ContactsSearchDefaultState: ContactsSearchShape = {
    contactListRequest: models.defaultContactsRequest, 
    contactListResponse: {
        ...models.defaultResponse,
        companies: new Array<models.DropDownListItem>(),
    },
    isContactListLoading: false,
    isContactsDownloading: false,
}

export interface State {
    contacts: ContactsStateShape;
    contactLists: ContactListsShape;  
    dropdowns: DropdownsShape; 
    search: ContactsSearchShape;
}

export type Action = ActionType<typeof actions>;

export default combineReducers<State, Action>({
    contacts: (state = contactsDefaultState, action) => {
        switch (action.type) {
            case constants.RECEIVE_CONTACT:             
                return {
                    ...state, 
                    contact: action.payload.contact,
                    isContactLoading: false, 
                }; 

            case constants.REQUEST_CONTACT: 
                return {
                    ...state,
                    contactId: action.payload.contactId,
                    isContactLoading: true, 
                }; 

            case constants.REQUEST_CONTACT_AUDITS: 
                return {
                    ...state, 
                    isContactAuditsLoading: true, 
                }; 
            case constants.RECEIVE_CONTACT_AUDITS: 
                return {
                    ...state, 
                    contactAuditLogs: action.payload.response,
                    isContactAuditsLoading: false, 
                }; 

            case constants.REQUEST_CONTACT_DUPES: 
                return {
                    ...state,
                    isContactDupesLoading: true,
                }; 
            case constants.RECEIVE_CONTACT_DUPES: 
                return {
                    ...state,
                    contactDupes: action.payload.response,
                    isContactDupesLoading: false,
                };
            case constants.REQUEST_CONTACT_DUPES_FAILURE:
                return {
                    ...state,
                    isContactDupesLoading: false,
                };

            case constants.REQUEST_CONTACT_MERGE:
                return {
                    ...state,
                    isContactMerging: true, 
                };
            case constants.RECEIVE_CONTACT_MERGE:
                return {
                    ...state,
                    isContactMerging: false, 
                };
            case constants.REQUEST_CONTACT_MERGE_FAILURE:
                return {
                    ...state,
                    isContactMerging: false,
                };
            
            case constants.REQUEST_CONTACT_LIST:
                return {
                    ...state, 
                    contactsResponse: models.defaultResponse, 
                    isContactListLoading: true, 
                }; 

            case constants.RECEIVE_CONTACT_LIST: 
                return {
                    ...state,
                    contactsResponse: action.payload.response, 
                    isContactListLoading: false, 
                }; 

            case constants.REQUEST_CONTACT_SYNC: 
                return {
                    ...state,
                    isContactSyncing: true,
                }; 
            case constants.RECEIVE_CONTACT_SYNC:
                return {
                    ...state,
                    isContactSyncing: false, 
                    contact: action.payload.response
                }

            case constants.UPDATE_CONTACT: 
                return {
                    ...state, 
                    isContactLoading: true, 
                }; 
            case constants.UPDATE_CONTACT_FAILURE: 
                return {
                    ...state, 
                    isContactLoading: false, 
                }; 
                
            default: 
                return state; 
        }
    },      
    contactLists: (state = contactListsDefaultState, action) => {
        switch (action.type) {
            case constants.REQUEST_COMPANY_EXAMINERS: 
                return {
                    ...state,
                    isCompanyExaminersListLoading: true,
                }; 

            case constants.RECEIVE_COMPANY_EXAMINERS: 
                return {
                    ...state,
                    companyExaminersList: action.payload.response,
                    isCompanyExaminersListLoading: false, 
                }; 
            
            case constants.REQUEST_EXAMINERS_LIST:
                return {
                    ...state,
                    isExaminersListLoading: true, 
                };  
            case constants.RECEIVE_EXAMINERS_LIST: 
                return {
                    ...state,
                    examinersList: action.payload.response,
                    isExaminersListLoading: false,
                }; 

            case constants.REQUEST_FIELD_ADJSTERS_LIST: 
                return {
                    ...state, 
                    isFieldAdjuestersListLoading: true, 
                }; 
            case constants.RECEIVE_FIELD_ADJSTERS_LIST: 
                return {
                    ...state, 
                    fieldAdjustersList: action.payload.response,
                    isFieldAdjuestersListLoading: false, 
                }; 

            case constants.REQUEST_FIELD_DIRECTORS_LIST: 
                return {
                    ...state,
                    isFieldDirectorsListLoading: true,
                }; 
            case constants.RECEIVE_FIELD_DIRECTORS_LIST: 
                return {
                    ...state,
                    fieldDirectorsList: action.payload.response,
                    isFieldAdjuestersListLoading: false,
                }; 

            case constants.REQUEST_REVIEWERS_LIST:
                return {
                    ...state,
                    isReviewersListLoading: true,
                }; 
            case constants.RECEIVE_REVIEWERS_LIST:
                return {
                    ...state,
                    reviewersList: action.payload.response,
                    isReviewersListLoading: false,
                }; 

            default: 
                return state;
        }
    }, 
    dropdowns: (state = DropDownDefaultState, action) => {
        switch (action.type) {
            case constants.REQUEST_ADJUSTERS_DD: 
                return {
                    ...state, 
                    isAdjustersDDLoading: true, 
                }

            case constants.RECEIVE_ADJUSTERS_DD: 
                return {
                    ...state, 
                    isAdjustersDDLoading: false, 
                    adjustersDD: action.payload.response, 
                }

            case constants.REQUEST_COMPANY_EXAMINERS_DD: 
                return {
                    ...state,
                    isCompanyExaminerDDLoading: true,
                };            
            case constants.RECEIVE_COMPANY_EXAMINERS_DD:
                return {
                    ...state, 
                    isCompanyExaminerDDLoading: false, 
                    companyExaminerDD: action.payload.response
                }; 

            case constants.REQUEST_EXAMINERS_DD: 
                return {
                    ...state, 
                    isExaminersDDLoading: true, 
                }

            case constants.RECEIVE_EXAMINERS_DD: 
                return {
                    ...state, 
                    isExaminersDDLoading: false, 
                    examinersDD: action.payload.response, 
                }

            case constants.REQUEST_FIELD_DIRECTORS_DD: 
                return {
                    ...state, 
                    isFieldDirectorsDDLoading: true, 
                }

            case constants.RECEIVE_FIELD_DIRECTORS_DD: 
                return {
                    ...state,
                    isFieldDirectorsDDLoading: false, 
                    fieldDirectorsDD: action.payload.response,
                }

            case constants.REQUEST_REVIEWERS_DD: 
                return {
                    ...state, 
                    isReviewersDDLoading: true, 
                }

            case constants.RECEIVE_REVIEWERS_DD: 
                return {
                    ...state, 
                    isReviewersDDLoading: false, 
                    reviewersDD: action.payload.response, 
                }

            default: 
                return state;
        }
    },
    search: (state = ContactsSearchDefaultState, action) => {
        switch (action.type) {
            case constants.REQUEST_CONTACT_LIST:
                return {
                    ...state, 
                    contactListRequest: action.payload.request,
                    contactListResponse: models.defaultResponse, 
                    isContactListLoading: true, 
                };
            case constants.RECEIVE_CONTACT_LIST:
                return {
                    ...state,
                    contactListResponse: action.payload.response, 
                    isContactListLoading: false,    
                };
            case constants.REQUEST_CONTACT_LIST_FAILURE: 
                return {
                    ...state, 
                    isContactListLoading: false, 
                }; 
            
            case constants.REQUEST_CONTACTS_CSV:
                return {
                    ...state,
                    isContactsDownloading: true,
                };
            case constants.RECEIVE_CONTACTS_CSV:
                return {
                    ...state,
                    isContactsDownloading: false,
                };
                
            default: 
                return state;
        }
    }
});
